// src/features/admin/components/settings/matter-configuration/components/EngagementConfigurationRootPanel.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useMatterNumbering } from '../../../../hooks/useMatterNumbering';
import type {
  EngagementNumberingConfig,
  ConfigurationSection
} from '../../../../../../types/matterNumbering';
import { PatternBuilder } from './PatternBuilder'; // Re-enabled with fixes
import { GlobalSettings } from './GlobalSettings';
import { TemplateManagement } from './TemplateManagement';

export interface EngagementConfigurationRootPanelProps {
  onSave?: (config: EngagementNumberingConfig) => void;
  onCancel?: () => void;
}

export const EngagementConfigurationRootPanel: React.FC<EngagementConfigurationRootPanelProps> = ({
  onCancel
}) => {
  const [activeSection, setActiveSection] = useState<ConfigurationSection>('builder');
  const [config, setConfig] = useState<EngagementNumberingConfig | null>(null);
  const [loading, setLoading] = useState(false);

  const { getConfig } = useMatterNumbering();

  // Load configuration on mount
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        setLoading(true);
        const loadedConfig = await getConfig();
        setConfig(loadedConfig);
      } catch (error) {
        console.error('Failed to load configuration:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfiguration();
  }, [getConfig]); // Depend on getConfig function

  // Simple pattern change handler - FIXED: wrapped in useCallback to prevent infinite loop
  const handlePatternChange = useCallback((pattern: string) => {
    if (config) {
      const updatedConfig = {
        ...config,
        defaultTemplate: {
          ...config.defaultTemplate,
          pattern
        }
      };
      setConfig(updatedConfig);
    }
  }, [config]); // Stable dependencies

  // Save function removed - individual templates are now saved via PatternBuilder

  // Handle config updates from TemplateManagement.
  // Child components that perform save operations pass the latest config back up for local UI sync.
  const handleConfigChange = useCallback(async (newConfig: EngagementNumberingConfig) => {
    setConfig(newConfig);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with save status and last saved indicator */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Engagement Configuration
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure subscriber-wide engagement numbering and reusable organization templates.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Save Configuration button removed - functionality moved to individual template saving */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Save error display removed as per mock save */}

      {/* Navigation tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'builder', label: 'Engagement Builder' },
            { id: 'templates', label: 'Engagement Templates' },
            { id: 'settings', label: 'Global Settings' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id as ConfigurationSection)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSection === tab.id
                  ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content sections */}
      <div className="min-h-[400px]">
        {activeSection === 'templates' && config && (
          <TemplateManagement
            config={config}
            onConfigChange={handleConfigChange}
          />
        )}

        {activeSection === 'builder' && config && (
          <PatternBuilder
            pattern={config.defaultTemplate.pattern}
            onPatternChange={handlePatternChange}
            config={config}
            onConfigChange={setConfig}
          />
        )}

        {activeSection === 'settings' && config && (
          <GlobalSettings
            settings={config.globalSettings}
            onChange={() => {}}
          />
        )}
      </div>
    </div>
  );
};
